import { AlineTransforms, createDefaultDocElement, htmlToAline } from '@aline/common';
import { convertMarkdownToHtml } from '@aline/common/serializers/markdown/markdown-to-html';
import copy from 'copy-to-clipboard';
import removeMarkdown from 'remove-markdown';
import { isDev } from 'src/config';
import { useMaybeEditingHooks } from 'src/editor/helpers/useEditingHooks';
import { Apollo } from 'src/gql/generated';
import { useCreateAndOpenNewDocument, useNotificationDispatch } from 'src/hooks';
import { ClipboardMime } from 'src/layout-engine';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

export function useAssistantMessageHandlers() {
  const dispatch = useNotificationDispatch();
  const { authorId } = useMaybeEditingHooks();
  const { createAndOpenNewDocument } = useCreateAndOpenNewDocument();

  async function createNewDocumentFromMarkdown(
    title: string,
    markdown: string,
    options: { aiChatItem?: string } = {}
  ) {
    const html = await convertMarkdownToHtml(markdown);
    const editor = AlineTransforms.createServerEditor({
      editingUserId: authorId,
      isInSigningCeremony: false,
      isReadOnly: false,
      isSigned: false,
    });
    const { blocks, listDefinitions } = htmlToAline(editor, { html });
    const documentElement = createDefaultDocElement({ children: blocks, listDefinitions });
    createAndOpenNewDocument({
      aiChatItem: options.aiChatItem,
      documentElement,
      name: title,
      variant: Apollo.DocumentVariant.Document,
    });
  }

  function notifyCopied(msg?: string) {
    dispatch({
      payload: {
        key: uuid4(),
        message: msg || 'AI message copied to clipboard.',
        variant: 'success',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  async function copyMarkdownToClipboard(markdown: string, msg?: string) {
    const plainText = removeMarkdown(markdown);
    const htmlText = await convertMarkdownToHtml(markdown);

    copy(plainText, {
      debug: isDev,
      format: ClipboardMime.text,
      onCopy: data => {
        if (data instanceof DataTransfer) {
          /**
           * Copy to HTML is important for a couple use cases:
           * 1. Copying AI messages into other apps (e.g., email) that support HTML.
           * 2. Copying AI messages into the Aline editor using htmlToAline function.
           */
          data.setData(ClipboardMime.html, htmlText);
        }
      },
    });
    notifyCopied(msg);
  }

  /**
   * Copy markdown that is still being resolved. Must be called synchronously
   * inside the click handler: it hands the pending promise to `ClipboardItem`
   * so the clipboard write starts within the gesture — Safari and Firefox drop
   * transient user activation across an `await`, so resolving the markdown
   * first and then copying silently fails there.
   */
  function copyMarkdownPromiseToClipboard(markdownPromise: Promise<string>, msg?: string) {
    const copyResolved = () =>
      void markdownPromise.then(markdown => copyMarkdownToClipboard(markdown, msg));

    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard) {
      copyResolved();
      return;
    }
    const item = new ClipboardItem({
      [ClipboardMime.html]: markdownPromise.then(markdown => convertMarkdownToHtml(markdown)),
      [ClipboardMime.text]: markdownPromise.then(markdown => removeMarkdown(markdown)),
    });
    navigator.clipboard
      .write([item])
      .then(() => notifyCopied(msg))
      // Best effort: browsers that reject promise-backed items still copy via execCommand.
      .catch(copyResolved);
  }

  return {
    copyMarkdownPromiseToClipboard,
    copyMarkdownToClipboard,
    createNewDocumentFromMarkdown,
  };
}
